import EngravingImage from './EngravingImage';
import './editorial.css';

/**
 * OperationalMap — a STATIC, print-styled operational map for the Brief.
 *
 * Fetches a real terrain render from the Mapbox Static Images API (same account /
 * public token as the live Geography map), framed to the day's Key Development
 * coordinates, and runs it through the shared engraving/halftone treatment
 * (EngravingImage). KD severity dots + location callouts are drawn as a SEPARATE
 * crisp overlay on top — projected with the same Web Mercator math used for the
 * static request, so they line up — rather than being baked into the map image.
 *
 * Fully client-side: no live web-map JS, no pins/legend chrome from Mapbox.
 * Falls back to the stored SVG (or a placeholder) when the token or coordinates
 * are unavailable, so it never renders blank.
 */

const TILE = 512;          // Mapbox tile size (Web Mercator, 512px tiles)
const MAP_W = 1200;        // logical request size (2:1); requested @2x for print
const MAP_H = 600;
const STYLE = process.env.REACT_APP_MAPBOX_STATIC_STYLE || 'mapbox/outdoors-v12';
const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const clampLat = (lat) => Math.max(-85.05, Math.min(85.05, lat));

// Latitude → normalized Web-Mercator Y in [0,1] (north = small).
function latToMercNorm(lat) {
  const s = Math.sin((clampLat(lat) * Math.PI) / 180);
  return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
}
function lngLatToWorld(lng, lat, z) {
  const scale = TILE * Math.pow(2, z);
  return { x: ((lng + 180) / 360) * scale, y: latToMercNorm(lat) * scale };
}

// Center + zoom that frame all points into MAP_W×MAP_H with padding.
export function framePoints(points, padFrac = 0.16, maxZoom = 6.5) {
  const lngs = points.map((p) => p.lng);
  const lats = points.map((p) => p.lat);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const centerLng = (minLng + maxLng) / 2;
  const centerLat = (minLat + maxLat) / 2;

  const lngFrac = (maxLng - minLng) / 360;
  const latFrac = Math.abs(latToMercNorm(minLat) - latToMercNorm(maxLat));
  const wPad = MAP_W * (1 - padFrac);
  const hPad = MAP_H * (1 - padFrac);

  let z;
  if (lngFrac <= 1e-9 && latFrac <= 1e-9) {
    z = 5; // single point (or all coincident)
  } else {
    const zLng = lngFrac > 1e-9 ? Math.log2(wPad / (TILE * lngFrac)) : maxZoom;
    const zLat = latFrac > 1e-9 ? Math.log2(hPad / (TILE * latFrac)) : maxZoom;
    z = Math.min(zLng, zLat);
  }
  z = Math.max(1, Math.min(maxZoom, z));
  return { centerLng, centerLat, zoom: Math.round(z * 100) / 100 };
}

// Project a point to a percentage position within the rendered image.
export function projectPercent(p, frame) {
  const c = lngLatToWorld(frame.centerLng, frame.centerLat, frame.zoom);
  const w = lngLatToWorld(p.lng, p.lat, frame.zoom);
  return {
    left: ((MAP_W / 2 + (w.x - c.x)) / MAP_W) * 100,
    top: ((MAP_H / 2 + (w.y - c.y)) / MAP_H) * 100,
  };
}

export function buildStaticUrl(frame) {
  return `https://api.mapbox.com/styles/v1/${STYLE}/static/`
    + `${frame.centerLng},${frame.centerLat},${frame.zoom}/${MAP_W}x${MAP_H}@2x`
    + `?access_token=${TOKEN}&attribution=false&logo=false`;
}

export default function OperationalMap({ points = [], threatColor, fallbackSvgUrl, alt = "Operational map — today's key developments" }) {
  const valid = (points || []).filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));

  // No token or no coordinates → fall back to the stored SVG (or a placeholder).
  if (!TOKEN || TOKEN === 'undefined' || valid.length === 0) {
    return fallbackSvgUrl
      ? <EngravingImage src={fallbackSvgUrl} alt={alt} />
      : <div className="bi-hero__placeholder" role="img" aria-label="Operational map" />;
  }

  const frame = framePoints(valid);
  const url = buildStaticUrl(frame);

  return (
    <div className="bi-opmap">
      <EngravingImage src={url} alt={alt} />
      <div className="bi-opmap__pins">
        {valid.map((p, i) => {
          const { left, top } = projectPercent(p, frame);
          if (left < -4 || left > 104 || top < -4 || top > 104) return null; // off-frame
          const color = (threatColor && threatColor(p.severity)) || 'var(--color-atomic)';
          const flip = left > 62; // near the right edge → label to the left of the badge
          const kd = p.kd || i + 1;
          return (
            <div key={i} className="bi-opmap__pin" style={{ left: `${left}%`, top: `${top}%` }}>
              <span className="bi-opmap__badge" style={{ background: color }}>{`KD${kd}`}</span>
              {p.label && (
                // Anchor to the badge's outer edge (100% of the badge box) + gap, so the
                // callout can never overlap the KD number regardless of badge width.
                <span className="bi-opmap__label" style={{ [flip ? 'right' : 'left']: 'calc(100% + 7px)', borderColor: color }}>
                  {p.label}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
